import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { _post } from '../../../utils/network';
import { commafy } from '../../../utils/methods';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ListTable from '../../../components/List/ListTable/ListTable.component';
import Pagination from '../../../components/List/Pagination/Pagination.component';

const UsersList = () => {
	const { t } = useTranslation();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('users'), link: '/users-list' },
		{ title: t('list') }
	];

	const [users, setUsers] = useState([]);
	let usersListData = {
		head: [t('fullname'), t('mobile'),, t('inviter_code'), t('score1_name'), t('score2_name'), t('status'), t('zar_formula'), t('operation')],
		body: users.map(user => [
			user.user_fullname,
			user.phone_number,
			user.inviter_code,
			commafy(user.user_rial_wallet) || 0,
			commafy(user.user_star_wallet) || 0,
			<span className={"badge rounded-pill p-2 badge-subtle-" + (user.is_active ? 'success' : 'secondary')}>
				{user.is_active ? t('active') : t('deactive')}
			</span>,
			<span className={"badge rounded-pill p-2 badge-subtle-" + (user.is_zar_formula_active ? 'success' : 'secondary')}>
				{user.is_zar_formula_active ? t('active') : t('deactive')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/users-edit/${user.user_id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['rtl', 'ltr', 'ltr', 'ltr', 'ltr', '', '', 'ltr']
	};

	const [pageNumber, setPageNumber] = useState(1);
	const [pageCounter] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [pagesCount, setPagesCount] = useState(0);
	const [searchUserFullname, setSearchUserFullname] = useState(null);
	const [searchUserMobile, setSearchUserMobile] = useState(null);
	const [searchUserInviterCode, setSearchUserInviterCode] = useState(null);
	const [searchUserNationalID, setSearchUserNationalID] = useState(null);
	const [searchUserTradeCode, setSearchUserTradeCode] = useState(null);
	const [searchUserActivation, setSearchUserActivation] = useState(null);
	const [searchButtonIsLoading, setSearchButtonIsLoading] = useState(false);
	const [searchCounter, setSearchCounter] = useState(0);
	const getUsers = async () => {
		let body = {
			user_fullname: searchUserFullname,
			phone_number: searchUserMobile,
			inviter_code: searchUserInviterCode,
			national_code: searchUserNationalID,
			trade_code: searchUserTradeCode,
			is_credit: null,
			is_active: searchUserActivation,
			page: pageNumber,
			count: pageCounter
		};
		setSearchButtonIsLoading(true);
		let response = await _post('/users/admin/getAll', body);
		setSearchButtonIsLoading(false);
		switch(response.code){
			case 200:
				setTotalCount(response.data.all_users);
				setUsers(response.data.users);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	const filterUsers = event => {
		event.preventDefault();
		if(searchButtonIsLoading)
			return 0;
		let fullname = event.target.searchUserFullname.value.trim() || null;
		let mobile = event.target.searchUserMobile.value.trim() || null;
		let inviterCode = event.target.searchUserInviterCode.value.trim() || null;
		let nationalID = event.target.searchUserNationalID.value.trim() || null;
		let tradeCode = event.target.searchUserTradeCode.value.trim() || null;
		let is_active = event.target.searchUserActivation.value.trim() || null;
		setSearchUserFullname(fullname);
		setSearchUserMobile(mobile);
		setSearchUserInviterCode(inviterCode);
		setSearchUserNationalID(nationalID);
		setSearchUserTradeCode(tradeCode);
		setSearchUserActivation(is_active);
		if(pageNumber === 1)
			setSearchCounter(searchCounter + 1);
		else
			setPageNumber(1);
	};

	const userActivationList = [
		{ key: t('deactive'), value: 0 },
		{ key: t('active'), value: 1 }
	];
	
	useEffect(() => {
		setPagesCount(Math.ceil(totalCount / pageCounter));
	}, [totalCount]);
	
	useEffect(() => {
		setSearchCounter(searchCounter + 1);
	}, [pageNumber]);

	useEffect(() => {
		getUsers();
	}, [searchCounter]);
	
	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('search_for')} {t('users')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={filterUsers}>
						<div className="row gx-2">
							<FormField fieldKey="searchUserFullname" title={t('fullname')} value={searchUserFullname} isSearch={true} />
							<FormField fieldKey="searchUserMobile" title={t('mobile')} value={searchUserMobile} isSearch={true} />
							<FormField fieldKey="searchUserInviterCode" title={t('inviter_code')} value={searchUserInviterCode} isSearch={true} />
							<FormField fieldKey="searchUserNationalID" title={t('national_id')} value={searchUserNationalID} isSearch={true} />
							<FormField fieldKey="searchUserTradeCode" title={t('trade_code')} value={searchUserTradeCode} isSearch={true} />
							<FormField type="select" fieldKey="searchUserActivation" title={t('status')} value={searchUserActivation} selectItems={userActivationList} isSearch={true} />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={searchButtonIsLoading}>
								<i className="fa fa-search"></i> {t('search')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
			<ListTable title={t('users')} pageNumber={pageNumber} pageCounter={pageCounter} totalCount={totalCount} data={usersListData} />
			<Pagination pagesCount={pagesCount} action={setPageNumber} />
		</>
	);
};

export default UsersList;
